import { useEffect, useRef, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { getPendingDraftStatic, syncDraftStatic, DraftData } from '@/hooks/useDraftProtection';
import { TOAST } from '@/lib/toastMessages';

const DEBUG = import.meta.env.DEV;

const log = (emoji: string, message: string, data?: any) => {
  if (!DEBUG) return;
  const style = 'color: #10b981; font-weight: bold;';
  if (data) {
    console.log(`%c[DraftSyncManager] ${emoji} ${message}`, style, data);
  } else {
    console.log(`%c[DraftSyncManager] ${emoji} ${message}`, style);
  }
};

export function DraftSyncManager() {
  const hasCheckedOnMount = useRef(false);
  const isSyncingRef = useRef(false);

  const attemptSync = useCallback(async (showToast = true): Promise<boolean> => {
    if (isSyncingRef.current) {
      log('⏳', 'Sync already in progress, skipping');
      return false;
    }
    
    const draft = getPendingDraftStatic();
    if (!draft) {
      log('📭', 'No pending draft to sync');
      return false;
    }

    isSyncingRef.current = true;
    log('🔄', 'Attempting sync', { sectionId: draft.sectionId });

    try {
      const success = await syncDraftStatic(draft);
      
      if (success && showToast) {
        log('✅', 'Sync completed successfully');
        toast(TOAST.DRAFT.SYNC_SUCCESS);
      } else if (!success && showToast) {
        log('❌', 'Sync failed');
        toast({ ...TOAST.DRAFT.SYNC_ERROR, variant: 'destructive' });
      }
      
      return success;
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  // Check for pending draft on mount
  useEffect(() => {
    if (hasCheckedOnMount.current) return;
    hasCheckedOnMount.current = true;

    const draft = getPendingDraftStatic();
    log('🚀', 'Mount check', { 
      hasPendingDraft: !!draft, 
      isOnline: navigator.onLine,
      sectionId: draft?.sectionId 
    });

    if (draft && navigator.onLine) {
      // Small delay to let the app initialize
      setTimeout(() => {
        log('⏰', 'Delayed sync triggered after mount');
        attemptSync(true);
      }, 1000);
    }
  }, [attemptSync]);

  // Listen for online event to auto-sync
  useEffect(() => {
    const handleOnline = () => {
      log('🌐', 'Online event detected');
      const draft = getPendingDraftStatic();
      if (draft) {
        log('📬', 'Found pending draft, scheduling sync', { sectionId: draft.sectionId });
        // Delay sync slightly to ensure connection is stable
        setTimeout(() => {
          attemptSync(true);
        }, 500);
      } else {
        log('📭', 'No pending draft found on online event');
      }
    };

    window.addEventListener('online', handleOnline);
    log('👂', 'Listening for online events');
    
    return () => {
      window.removeEventListener('online', handleOnline);
      log('🔇', 'Stopped listening for online events');
    };
  }, [attemptSync]);

  // Expose sync function globally for OfflineBanner to use
  useEffect(() => {
    (window as any).__draftSyncManager = {
      attemptSync,
      hasPendingDraft: () => getPendingDraftStatic() !== null,
    };
    log('🔧', 'Exposed global sync manager');

    return () => {
      delete (window as any).__draftSyncManager;
    };
  }, [attemptSync]);

  return null;
}
